import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Parameter } from '@shared/types';
import { ViewBox } from '@/utils/svgPathParser';
import {
  isMeasurementParameter,
  calculateParameterRange,
  translateParameterLabel,
} from '@/utils/parameterUtils';
import { DIMENSION, COLORS, DRAWING_COLORS, type RenderMode } from './constants';
import type { ProjectionView } from '@/hooks/useProjectionViews';

const DRAG_RANGE_PIXELS = 500;
const CLICK_THRESHOLD = 4;

type DimensionCategory = 'overall' | 'hole' | 'radius' | 'thickness' | 'spacing' | 'depth';

type DimensionGroupProps = {
  view?: ProjectionView;
  viewBox: ViewBox;
  parameters: Parameter[];
  renderMode?: RenderMode;
  onParameterChange?: (param: Parameter, value: number) => void;
  onParameterCommit?: (param: Parameter, value: number) => void;
};

type MatchedDimension = {
  param: Parameter;
  orientation: 'horizontal' | 'vertical';
  kind: 'width' | 'height';
  category: DimensionCategory;
  stackIndex: number;
  tolerance?: string;
};

type ScrewHoleGroup = {
  name: string;
  params: { role: 'pilot' | 'clearance' | 'countersink' | 'counterbore'; param: Parameter }[];
};

// Categorization patterns (English + Japanese keywords)
const HOLE_TERMS = ['hole', 'bore', 'drill', 'pilot', 'clearance', 'countersink', 'counterbore', '穴', '下穴', '皿穴', '座ぐり'];
const RADIUS_TERMS = ['radius', 'fillet', 'chamfer', 'round', 'corner_r', '半径', 'フィレット', '面取り', '丸み'];
const DIAMETER_TERMS = ['diameter', 'dia', '直径'];
const THICKNESS_TERMS = ['thickness', 'wall', 'shell', '厚さ', '壁'];
const SPACING_TERMS = ['spacing', 'gap', 'pitch', 'distance', 'offset', '間隔', '隙間', 'ピッチ'];
const DEPTH_TERMS = ['depth', '奥行き'];
const WIDTH_TERMS = ['width', 'length', 'size', '幅', '長さ', 'サイズ'];
const HEIGHT_TERMS = ['height', '高さ'];

// Default manufacturing tolerances by category
const DEFAULT_TOLERANCES: Partial<Record<DimensionCategory, string>> = {
  hole: '±0.1',
  radius: '±0.05',
};

// Screw hole role keywords
const PILOT_TERMS = ['pilot', '下穴'];
const CLEARANCE_TERMS = ['clearance', 'クリアランス'];
const COUNTERSINK_TERMS = ['countersink', '皿穴'];
const COUNTERBORE_TERMS = ['counterbore', '座ぐり'];

function categorizeParameter(param: Parameter): DimensionCategory {
  const name = param.name.toLowerCase();
  const display = param.displayName.toLowerCase();
  const check = (terms: string[]) => terms.some((t) => name.includes(t) || display.includes(t));

  if (check(HOLE_TERMS) || check(DIAMETER_TERMS)) return 'hole';
  if (check(RADIUS_TERMS)) return 'radius';
  if (check(THICKNESS_TERMS)) return 'thickness';
  if (check(SPACING_TERMS)) return 'spacing';
  if (check(DEPTH_TERMS)) return 'depth';
  return 'overall';
}

function getOrientation(param: Parameter, category: DimensionCategory): 'horizontal' | 'vertical' {
  const name = param.name.toLowerCase();
  const display = param.displayName.toLowerCase();
  const check = (terms: string[]) => terms.some((t) => name.includes(t) || display.includes(t));

  if (check(HEIGHT_TERMS) || category === 'depth') return 'vertical';
  if (check(WIDTH_TERMS)) return 'horizontal';
  if (category === 'thickness') return 'vertical';
  return 'horizontal';
}

function findToleranceParam(param: Parameter, allParams: Parameter[]): string | undefined {
  const baseName = param.name.replace(/_?(diameter|radius|size|width|height|depth|thickness|hole)$/i, '');
  const toleranceParam = allParams.find((p) => {
    const n = p.name.toLowerCase();
    return (
      (n === `${baseName}_tolerance` || n === `${param.name}_tolerance` || n === `${baseName}tolerance`) &&
      p.type === 'number'
    );
  });
  if (toleranceParam) return `±${Number(toleranceParam.value)}`;
  return undefined;
}

function detectScrewHoleGroups(params: Parameter[]): ScrewHoleGroup[] {
  const groups: Map<string, ScrewHoleGroup> = new Map();
  const check = (name: string, terms: string[]) => terms.some((t) => name.includes(t));

  for (const param of params) {
    if (param.type !== 'number' && param.type !== undefined) continue;
    const name = param.name.toLowerCase();
    const display = param.displayName.toLowerCase();

    let role: 'pilot' | 'clearance' | 'countersink' | 'counterbore' | null = null;
    if (check(name, PILOT_TERMS) || check(display, PILOT_TERMS)) role = 'pilot';
    else if (check(name, CLEARANCE_TERMS) || check(display, CLEARANCE_TERMS)) role = 'clearance';
    else if (check(name, COUNTERSINK_TERMS) || check(display, COUNTERSINK_TERMS)) role = 'countersink';
    else if (check(name, COUNTERBORE_TERMS) || check(display, COUNTERBORE_TERMS)) role = 'counterbore';

    if (!role) continue;

    // Extract group prefix (e.g., "screw_" from "screw_pilot_diameter")
    const prefix = name
      .replace(/_(pilot|clearance|countersink|counterbore).*$/i, '')
      .replace(/(pilot|clearance|countersink|counterbore)/i, '')
      .replace(/_+$/, '') || 'screw';

    if (!groups.has(prefix)) {
      groups.set(prefix, { name: prefix, params: [] });
    }
    groups.get(prefix)!.params.push({ role, param });
  }

  // Only return groups with at least 2 related params
  return Array.from(groups.values()).filter((g) => g.params.length >= 2);
}

function matchParametersToDimensions(params: Parameter[]): MatchedDimension[] {
  const measurement = params.filter(isMeasurementParameter);
  const matched: MatchedDimension[] = [];
  let hCount = 0;
  let vCount = 0;

  for (const param of measurement) {
    const category = categorizeParameter(param);
    const orientation = getOrientation(param, category);
    const tolerance =
      findToleranceParam(param, params) ?? DEFAULT_TOLERANCES[category];

    if (orientation === 'horizontal') {
      matched.push({ param, orientation, kind: 'width', category, stackIndex: hCount++, tolerance });
    } else {
      matched.push({ param, orientation, kind: 'height', category, stackIndex: vCount++, tolerance });
    }
  }
  return matched;
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, val));
}

function formatLabel(dim: MatchedDimension, val: number): string {
  const valStr = Number.isInteger(val) ? val.toString() : val.toFixed(1);
  if (dim.category === 'hole') return `⌀${valStr}`;
  if (dim.category === 'radius') return `R${valStr}`;
  return valStr;
}

function getDisplayName(param: Parameter): string {
  if (param.description && /[\u3000-\u9fff]/.test(param.description)) {
    return param.description;
  }
  // Prefer Japanese displayName if available
  if (param.displayName && /[\u3000-\u9fff]/.test(param.displayName)) {
    return param.displayName;
  }
  return translateParameterLabel(
    param.description || param.displayName || param.name,
  );
}

export function DimensionGroup({
  view = 'front',
  viewBox: vb,
  parameters,
  renderMode,
  onParameterChange,
  onParameterCommit,
}: DimensionGroupProps) {
  const isDrawingMode = renderMode === 'drawing';
  const colors = isDrawingMode ? DRAWING_COLORS : COLORS;
  const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);
  const [editingParam, setEditingParam] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dragRef = useRef<{
    dim: MatchedDimension;
    startClientX: number;
    startClientY: number;
    startVal: number;
    sensitivity: number;
    range: { min: number; max: number };
    pendingVal: number;
  } | null>(null);
  const rafIdRef = useRef<number>(0);
  const [dragDisplay, setDragDisplay] = useState<{
    paramName: string;
    value: number;
  } | null>(null);

  const onChangeRef = useRef(onParameterChange);
  const onCommitRef = useRef(onParameterCommit);
  onChangeRef.current = onParameterChange;
  onCommitRef.current = onParameterCommit;

  const allMatched = useMemo(() => matchParametersToDimensions(parameters), [parameters]);
  const screwHoleGroups = useMemo(() => detectScrewHoleGroups(parameters), [parameters]);

  // Filter dimensions by view
  const matched = useMemo(() => {
    if (view === 'top') {
      return allMatched.filter((d) => d.orientation === 'horizontal');
    }
    if (view === 'right') {
      return allMatched.filter((d) => d.orientation === 'vertical');
    }
    return allMatched; // front shows all
  }, [allMatched, view]);

  const { offsetFromGeometry, stackSpacing, extensionGap, extensionOvershoot, fontSize, pillPadding, leaderLength, calloutFontSize, toleranceFontSize } = DIMENSION;

  useEffect(() => {
    return () => {
      if (dragRef.current) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (editingParam && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingParam]);

  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      const drag = dragRef.current;
      if (!drag) return;
      setDragDisplay({ paramName: drag.dim.param.name, value: drag.pendingVal });
      onChangeRef.current?.(drag.dim.param, drag.pendingVal);
    });
  }, []);

  const handleDragStart = useCallback(
    (dim: MatchedDimension, e: React.MouseEvent) => {
      if (isDrawingMode || editingParam) return;
      e.preventDefault();
      e.stopPropagation();

      const range = calculateParameterRange(dim.param);
      const sensitivity = (range.max - range.min) / DRAG_RANGE_PIXELS;
      const startVal = Number(dim.param.value);

      dragRef.current = {
        dim,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startVal,
        sensitivity,
        range,
        pendingVal: startVal,
      };
      setDragDisplay({ paramName: dim.param.name, value: startVal });

      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      const handleMove = (ev: MouseEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const pixelDelta =
          drag.dim.orientation === 'horizontal'
            ? ev.clientX - drag.startClientX
            : -(ev.clientY - drag.startClientY);
        const raw = drag.startVal + pixelDelta * drag.sensitivity;
        const clamped = clamp(raw, drag.range.min, drag.range.max);
        drag.pendingVal = Math.round(clamped * 100) / 100;
        scheduleUpdate();
      };

      const handleUp = (ev: MouseEvent) => {
        const drag = dragRef.current;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        if (drag) {
          const totalMovement =
            Math.abs(ev.clientX - drag.startClientX) +
            Math.abs(ev.clientY - drag.startClientY);
          if (totalMovement < CLICK_THRESHOLD) {
            setEditingParam(drag.dim.param.name);
          } else {
            onCommitRef.current?.(drag.dim.param, drag.pendingVal);
          }
        }

        dragRef.current = null;
        setDragDisplay(null);
        setHoveredHandle(null);
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = 0;
        }
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [editingParam, scheduleUpdate],
  );

  const getDisplayVal = useCallback(
    (dim: MatchedDimension) => {
      if (dragDisplay && dragDisplay.paramName === dim.param.name)
        return dragDisplay.value;
      return Number(dim.param.value);
    },
    [dragDisplay],
  );

  const handleEditSubmit = useCallback(
    (paramName: string, inputValue: string) => {
      const dim = matched.find((m) => m.param.name === paramName);
      if (!dim) return;
      const parsed = parseFloat(inputValue);
      if (!isNaN(parsed)) {
        const range = calculateParameterRange(dim.param);
        const clamped = clamp(parsed, range.min, range.max);
        onCommitRef.current?.(dim.param, clamped);
      }
      setEditingParam(null);
    },
    [matched],
  );

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, paramName: string) => {
      if (e.key === 'Enter') {
        handleEditSubmit(paramName, e.currentTarget.value);
      } else if (e.key === 'Escape') {
        setEditingParam(null);
      }
    },
    [handleEditSubmit],
  );

  if (matched.length === 0 && screwHoleGroups.length === 0) return null;

  const isDragging = dragDisplay !== null;

  // Determine if we should show screw hole callout (only on front view)
  const showScrewCallout = view === 'front' && screwHoleGroups.length > 0;

  return (
    <g className="dimension-group" style={{ pointerEvents: 'none' }}>
      {matched.map((dim) => {
        const val = getDisplayVal(dim);
        const paramDragging = dragDisplay?.paramName === dim.param.name;
        const isHovered = hoveredHandle === dim.param.name;
        const isEditing = editingParam === dim.param.name;
        const highlight = paramDragging || isHovered;
        const isLeader = dim.category === 'hole' || dim.category === 'radius';
        const labelText = formatLabel(dim, val);
        const unitText = ' mm';
        const fullText = `${labelText}${unitText}`;
        const toleranceText = dim.tolerance ?? '';
        const offset = offsetFromGeometry + dim.stackIndex * stackSpacing;

        const lineColor = highlight ? colors.dimensionActive : colors.dimension;
        const strokeW = highlight ? 0.35 : 0.25;
        const paramDisplayName = getDisplayName(dim.param);

        // Leader line rendering for holes and radii
        if (isLeader && view === 'front') {
          const cx = vb.minX + vb.width / 2;
          const cy = vb.minY + vb.height / 2;
          const endX = cx + leaderLength;
          const endY = cy - leaderLength * 0.6 - dim.stackIndex * 7;
          const shelfLen = leaderLength * 1.2;
          const textW = fullText.length * calloutFontSize * 0.55;

          return (
            <g key={dim.param.name}>
              {/* Leader line from center to shelf */}
              <line
                x1={cx} y1={cy} x2={endX} y2={endY}
                stroke={lineColor} strokeWidth={strokeW}
                markerStart="url(#leader-arrow)"
              />
              {/* Shelf line */}
              <line
                x1={endX} y1={endY} x2={endX + shelfLen} y2={endY}
                stroke={lineColor} strokeWidth={strokeW}
              />
              {/* Label */}
              <g
                style={{ pointerEvents: 'auto', cursor: isDragging ? undefined : 'grab' }}
                onMouseEnter={() => !isDragging && setHoveredHandle(dim.param.name)}
                onMouseLeave={() => !isDragging && setHoveredHandle(null)}
                onMouseDown={(e) => handleDragStart(dim, e)}
              >
                {isEditing ? (
                  <foreignObject
                    x={endX + 1}
                    y={endY - calloutFontSize - 1}
                    width={textW + 8}
                    height={calloutFontSize + 4}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      defaultValue={val.toFixed(1)}
                      onKeyDown={(e) => handleEditKeyDown(e, dim.param.name)}
                      onBlur={(e) => handleEditSubmit(dim.param.name, e.currentTarget.value)}
                      style={{
                        width: '100%', height: '100%',
                        border: 'none', outline: 'none',
                        background: '#FFFFFF', color: colors.dimension,
                        textAlign: 'left', fontSize: '10px',
                        fontWeight: 600, padding: '0 2px', boxSizing: 'border-box',
                      }}
                    />
                  </foreignObject>
                ) : (
                  <>
                    <text
                      x={endX + 1} y={endY - 1.5}
                      dominantBaseline="auto"
                      fill={highlight ? colors.dimensionActive : colors.dimension}
                      fontSize={calloutFontSize} fontWeight="600"
                      fontFamily="'Noto Sans JP', sans-serif"
                    >
                      {fullText}
                    </text>
                    {toleranceText && (
                      <text
                        x={endX + 1 + textW + 1.5} y={endY - 1.5}
                        dominantBaseline="auto"
                        fill={colors.titleBlock}
                        fontSize={toleranceFontSize} fontWeight="400"
                        fontFamily="'Noto Sans JP', sans-serif"
                      >
                        {toleranceText}
                      </text>
                    )}
                    {/* Parameter name annotation */}
                    <text
                      x={endX + 1} y={endY + calloutFontSize * 0.6}
                      dominantBaseline="hanging"
                      fill={COLORS.titleBlock}
                      fontSize={toleranceFontSize} fontWeight="400"
                      fontFamily="'Noto Sans JP', sans-serif"
                      opacity={0.7}
                    >
                      ({paramDisplayName})
                    </text>
                  </>
                )}
                {/* Hit target */}
                <rect
                  x={endX} y={endY - calloutFontSize - 2}
                  width={shelfLen + 4} height={calloutFontSize + 8}
                  fill="transparent"
                />
              </g>
            </g>
          );
        }

        // Standard dimension line rendering
        if (dim.orientation === 'horizontal') {
          const x1 = vb.minX;
          const x2 = vb.minX + vb.width;
          const lineY = vb.minY + vb.height + offset;
          const textX = (x1 + x2) / 2;
          const textY = lineY;
          const textW = fullText.length * fontSize * 0.55;

          return (
            <g key={dim.param.name}>
              {/* Extension lines */}
              <line
                x1={x1} y1={vb.minY + vb.height + extensionGap}
                x2={x1} y2={lineY + extensionOvershoot}
                stroke={lineColor} strokeWidth={strokeW * 0.7} opacity={0.6}
              />
              <line
                x1={x2} y1={vb.minY + vb.height + extensionGap}
                x2={x2} y2={lineY + extensionOvershoot}
                stroke={lineColor} strokeWidth={strokeW * 0.7} opacity={0.6}
              />

              {/* Dimension line */}
              <line
                x1={x1} y1={lineY} x2={x2} y2={lineY}
                stroke={lineColor} strokeWidth={strokeW}
                markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)"
              />

              {/* Interactive label */}
              <g
                style={{ pointerEvents: 'auto', cursor: isDragging ? undefined : 'grab' }}
                onMouseEnter={() => !isDragging && setHoveredHandle(dim.param.name)}
                onMouseLeave={() => !isDragging && setHoveredHandle(null)}
                onMouseDown={(e) => handleDragStart(dim, e)}
              >
                <rect
                  x={textX - textW / 2 - pillPadding.x}
                  y={textY - fontSize / 2 - pillPadding.y}
                  width={textW + pillPadding.x * 2}
                  height={fontSize + pillPadding.y * 2}
                  rx={fontSize * 0.3}
                  fill={isDrawingMode ? 'none' : (highlight ? colors.dimensionActive : '#FFFFFF')}
                  fillOpacity={0.95}
                  stroke={lineColor}
                  strokeWidth={0.15}
                />

                {isEditing ? (
                  <foreignObject
                    x={textX - textW / 2 - pillPadding.x}
                    y={textY - fontSize / 2 - pillPadding.y}
                    width={textW + pillPadding.x * 2}
                    height={fontSize + pillPadding.y * 2}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      defaultValue={val.toFixed(1)}
                      onKeyDown={(e) => handleEditKeyDown(e, dim.param.name)}
                      onBlur={(e) => handleEditSubmit(dim.param.name, e.currentTarget.value)}
                      style={{
                        width: '100%', height: '100%',
                        border: 'none', outline: 'none',
                        background: '#FFFFFF', color: colors.dimension,
                        textAlign: 'center', fontSize: '12px',
                        fontWeight: 600, borderRadius: '3px',
                        padding: '0 2px', boxSizing: 'border-box',
                      }}
                    />
                  </foreignObject>
                ) : (
                  <>
                    <text
                      x={textX} y={textY}
                      textAnchor="middle" dominantBaseline="central"
                      fill={highlight ? (isDrawingMode ? colors.dimension : 'white') : colors.dimension}
                      fontSize={fontSize} fontWeight="600"
                      fontFamily="'Noto Sans JP', sans-serif"
                    >
                      {fullText}
                    </text>
                    {toleranceText && (
                      <text
                        x={textX + textW / 2 + pillPadding.x + 1}
                        y={textY + 0.5}
                        dominantBaseline="central"
                        fill={colors.titleBlock}
                        fontSize={toleranceFontSize} fontWeight="400"
                        fontFamily="'Noto Sans JP', sans-serif"
                      >
                        {toleranceText}
                      </text>
                    )}
                  </>
                )}

                {/* Parameter name annotation below dimension */}
                {!isEditing && (
                  <text
                    x={textX} y={textY + fontSize / 2 + pillPadding.y + toleranceFontSize}
                    textAnchor="middle" dominantBaseline="hanging"
                    fill={COLORS.titleBlock}
                    fontSize={toleranceFontSize} fontWeight="400"
                    fontFamily="'Noto Sans JP', sans-serif"
                    opacity={0.6}
                  >
                    {paramDisplayName}
                  </text>
                )}

                {/* Hit target */}
                <rect
                  x={x1} y={lineY - fontSize}
                  width={x2 - x1} height={fontSize * 3}
                  fill="transparent"
                />
              </g>
            </g>
          );
        } else {
          // Vertical dimension
          const y1 = vb.minY;
          const y2 = vb.minY + vb.height;
          const lineX = vb.minX + vb.width + offset;
          const textX = lineX;
          const textY = (y1 + y2) / 2;
          const textW = fullText.length * fontSize * 0.55;

          return (
            <g key={dim.param.name}>
              {/* Extension lines */}
              <line
                x1={vb.minX + vb.width + extensionGap} y1={y1}
                x2={lineX + extensionOvershoot} y2={y1}
                stroke={lineColor} strokeWidth={strokeW * 0.7} opacity={0.6}
              />
              <line
                x1={vb.minX + vb.width + extensionGap} y1={y2}
                x2={lineX + extensionOvershoot} y2={y2}
                stroke={lineColor} strokeWidth={strokeW * 0.7} opacity={0.6}
              />

              {/* Dimension line */}
              <line
                x1={lineX} y1={y1} x2={lineX} y2={y2}
                stroke={lineColor} strokeWidth={strokeW}
                markerStart="url(#dim-arrow-start)" markerEnd="url(#dim-arrow-end)"
              />

              {/* Interactive label */}
              <g
                style={{ pointerEvents: 'auto', cursor: isDragging ? undefined : 'grab' }}
                onMouseEnter={() => !isDragging && setHoveredHandle(dim.param.name)}
                onMouseLeave={() => !isDragging && setHoveredHandle(null)}
                onMouseDown={(e) => handleDragStart(dim, e)}
              >
                <rect
                  x={textX - fontSize / 2 - pillPadding.y}
                  y={textY - textW / 2 - pillPadding.x}
                  width={fontSize + pillPadding.y * 2}
                  height={textW + pillPadding.x * 2}
                  rx={fontSize * 0.3}
                  fill={isDrawingMode ? 'none' : (highlight ? colors.dimensionActive : '#FFFFFF')}
                  fillOpacity={0.95}
                  stroke={lineColor}
                  strokeWidth={0.15}
                  transform={`rotate(-90, ${textX}, ${textY})`}
                />

                {isEditing ? (
                  <foreignObject
                    x={textX - fontSize}
                    y={textY - textW / 2 - pillPadding.x}
                    width={fontSize * 2}
                    height={textW + pillPadding.x * 2}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      defaultValue={val.toFixed(1)}
                      onKeyDown={(e) => handleEditKeyDown(e, dim.param.name)}
                      onBlur={(e) => handleEditSubmit(dim.param.name, e.currentTarget.value)}
                      style={{
                        width: '100%', height: '100%',
                        border: 'none', outline: 'none',
                        background: '#FFFFFF', color: colors.dimension,
                        textAlign: 'center', fontSize: '12px',
                        fontWeight: 600, borderRadius: '3px',
                        padding: '0 2px', boxSizing: 'border-box',
                      }}
                    />
                  </foreignObject>
                ) : (
                  <>
                    <text
                      x={textX} y={textY}
                      textAnchor="middle" dominantBaseline="central"
                      fill={highlight ? (isDrawingMode ? colors.dimension : 'white') : colors.dimension}
                      fontSize={fontSize} fontWeight="600"
                      fontFamily="'Noto Sans JP', sans-serif"
                      transform={`rotate(-90, ${textX}, ${textY})`}
                    >
                      {fullText}
                    </text>
                    {toleranceText && (
                      <text
                        x={textX} y={textY + textW / 2 + pillPadding.x + toleranceFontSize + 1}
                        textAnchor="middle" dominantBaseline="central"
                        fill={colors.titleBlock}
                        fontSize={toleranceFontSize} fontWeight="400"
                        fontFamily="'Noto Sans JP', sans-serif"
                        transform={`rotate(-90, ${textX}, ${textY})`}
                      >
                        {toleranceText}
                      </text>
                    )}
                  </>
                )}

                {/* Parameter name annotation beside vertical dimension */}
                {!isEditing && (
                  <text
                    x={textX + fontSize / 2 + pillPadding.y + 1} y={textY}
                    textAnchor="middle" dominantBaseline="central"
                    fill={COLORS.titleBlock}
                    fontSize={toleranceFontSize} fontWeight="400"
                    fontFamily="'Noto Sans JP', sans-serif"
                    transform={`rotate(-90, ${textX + fontSize / 2 + pillPadding.y + 1}, ${textY})`}
                    opacity={0.6}
                  >
                    {paramDisplayName}
                  </text>
                )}

                {/* Hit target */}
                <rect
                  x={lineX - fontSize}
                  y={y1}
                  width={fontSize * 3}
                  height={y2 - y1}
                  fill="transparent"
                />
              </g>
            </g>
          );
        }
      })}

      {/* Screw hole group callout boxes */}
      {showScrewCallout && screwHoleGroups.map((group, gIdx) => {
        const boxX = vb.minX + vb.width + offsetFromGeometry + (allMatched.filter((d) => d.orientation === 'vertical').length) * stackSpacing + 8;
        const boxY = vb.minY + gIdx * 30;
        const boxW = 45;
        const lineH = calloutFontSize + 2;
        const boxH = (group.params.length + 1) * lineH + 4;
        const roleLabels: Record<string, string> = {
          pilot: '下穴',
          clearance: 'クリアランス穴',
          countersink: '皿穴',
          counterbore: '座ぐり穴',
        };

        // Map common English group prefixes to Japanese
        const groupNameMap: Record<string, string> = {
          screw: 'ネジ',
          bolt: 'ボルト',
          dowel: 'ダボ',
          pin: 'ピン',
          rivet: 'リベット',
        };
        const displayGroupName =
          groupNameMap[group.name.toLowerCase()] ||
          translateParameterLabel(group.name);

        return (
          <g key={group.name}>
            {/* Leader line from geometry center to callout */}
            <line
              x1={vb.minX + vb.width / 2} y1={vb.minY + vb.height / 2}
              x2={boxX} y2={boxY + boxH / 2}
              stroke={colors.dimension} strokeWidth={0.2}
              strokeDasharray="2,1"
              markerStart="url(#center-dot)"
            />
            {/* Callout box */}
            <rect
              x={boxX} y={boxY}
              width={boxW} height={boxH}
              fill="#FFFFFF" fillOpacity={0.95}
              stroke={colors.dimension} strokeWidth={0.2}
              rx={0.5}
            />
            {/* Group title */}
            <text
              x={boxX + boxW / 2} y={boxY + lineH / 2 + 2}
              textAnchor="middle" dominantBaseline="central"
              fill={colors.dimension}
              fontSize={calloutFontSize} fontWeight="700"
              fontFamily="'Noto Sans JP', sans-serif"
            >
              {displayGroupName}穴 詳細
            </text>
            {/* Divider */}
            <line
              x1={boxX + 1} y1={boxY + lineH + 2}
              x2={boxX + boxW - 1} y2={boxY + lineH + 2}
              stroke={colors.dimension} strokeWidth={0.15}
            />
            {/* Parameters */}
            {group.params.map((gp, pIdx) => {
              const py = boxY + (pIdx + 1) * lineH + lineH / 2 + 2;
              const val = Number(gp.param.value);
              return (
                <text
                  key={gp.role}
                  x={boxX + 2} y={py}
                  dominantBaseline="central"
                  fill={colors.text}
                  fontSize={toleranceFontSize} fontWeight="400"
                  fontFamily="'Noto Sans JP', sans-serif"
                >
                  {roleLabels[gp.role] || gp.role}: ⌀{val.toFixed(1)} mm
                </text>
              );
            })}
          </g>
        );
      })}
    </g>
  );
}
